import { useContext, useRef, useState, useEffect, useMemo } from "react";
import SingleDropDown from "../SingleDropDown";
import { useStyles } from "../../Styles/styles";
import clsx from "clsx";
import { SetupContext } from "../../Contexts/SetupContext";
import { UserContext } from "../../Contexts/UserContext";
import { styled } from "styled-components";
import { isRaceDataPresent } from "../../utils/utils";
import UseOfForce from "./UseOfForce";
import { FormHelperText } from "@material-ui/core";

const StyledHeader = styled.h4`
  text-align: center;
  width: 100%;
  color: ${props => (props.$nightMode ? "white" : "black")};
  margin: 0;
`;
const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  border: 1px solid grey;
  margin: 10px;
`;

const PassengerContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const defaultErrorFunction = () => {
  console.error("No function passed to RaceDataUoF");
};

const RaceDataUof = ({
  ticket = {},
  locked = true,
  handleChange = defaultErrorFunction,
  userRoles = [],
  outcomeOfStopError = "",
  ...props
}) => {
  const classes = useStyles();
  const { setup } = useContext(SetupContext);
  const { nightMode } = useContext(UserContext);
  const {
    perceivedRaces,
    ticketStopTypes,
    ticketSearchTypes,
    ticketEvidenceTypes,
    ticketOutcomeTypes,
    passengerTicketSearchTypes,
    passengerTicketEvidenceTypes,
    passengerTicketOutcomeTypes,
  } = setup.setupLists;

  const {
    perceivedRace,
    ticketStopType,
    ticketSearchType,
    passengerTicketSearchType,
    ticketEvidenceType,
    passengerTicketEvidenceType,
    ticketOutcomeType,
    passengerTicketOutcomeType,
    uofInstance,
    uofInstanceTypes,
    uofInjuryDetails,
  } = ticket || {};

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  const localChange = e => {
    handleChange(e, true, false);
  };

  const raceDataPresent = useMemo(() => isRaceDataPresent(ticket), [ticket]);
  return (
    <>
      <StyledHeader $nightMode={nightMode}>
        Race/UOF Data Collection
      </StyledHeader>
      <FlexContainer>
        <SingleDropDown
          disabled={locked}
          handleChange={localChange}
          stateName={"perceivedRace"}
          value={perceivedRace}
          options={perceivedRaces}
          title={"Perceived\xa0Race"}
          classStyle={clsx(
            classes.formControl,
            classes.fixedWidth,
            classes.fitHeight,
            raceDataPresent && !perceivedRace ? classes.vcvc_required : {},
          )}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          disabled={locked}
          handleChange={localChange}
          stateName={"ticketStopType"}
          value={ticketStopType}
          options={ticketStopTypes}
          title={"Stop\xa0Based\xa0On"}
          classStyle={clsx(
            classes.formControl,
            classes.fixedWidth,
            classes.fitHeight,
            raceDataPresent && !ticketStopType ? classes.vcvc_required : {},
          )}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <PassengerContainer>
          <SingleDropDown
            disabled={locked}
            handleChange={localChange}
            stateName={"ticketSearchType"}
            value={ticketSearchType}
            options={ticketSearchTypes}
            title={"Search\xa0Based\xa0On"}
            classStyle={clsx(
              classes.formControl,
              classes.fixedWidth,
              raceDataPresent && !ticketSearchType ? classes.vcvc_required : {},
            )}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <SingleDropDown
            disabled={locked}
            handleChange={localChange}
            stateName={"passengerTicketSearchType"}
            value={passengerTicketSearchType}
            options={passengerTicketSearchTypes}
            title={"Passenger\xa0Search\xa0Based\xa0On"}
            classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
        </PassengerContainer>
        <PassengerContainer>
          <SingleDropDown
            disabled={locked}
            handleChange={localChange}
            stateName={"ticketEvidenceType"}
            value={ticketEvidenceType}
            options={ticketEvidenceTypes}
            title={"Contraband/Evidence"}
            classStyle={clsx(
              classes.formControl,
              classes.fixedWidth,
              raceDataPresent && !ticketEvidenceType
                ? classes.vcvc_required
                : {},
            )}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <SingleDropDown
            disabled={locked}
            handleChange={localChange}
            stateName={"passengerTicketEvidenceType"}
            value={passengerTicketEvidenceType}
            options={passengerTicketEvidenceTypes}
            title={"Passenger\xa0Contra./Evid."}
            classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
        </PassengerContainer>
        <PassengerContainer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              width: "200px",
              maxWidth: "200px",
            }}
          >
            <SingleDropDown
              disabled={locked}
              handleChange={localChange}
              stateName={"ticketOutcomeType"}
              value={ticketOutcomeType}
              options={ticketOutcomeTypes}
              title={"Outcome\xa0Of\xa0Stop"}
              classStyle={clsx(
                classes.formControl,
                classes.fixedWidth,
                raceDataPresent && !ticketOutcomeType
                  ? classes.vcvc_required
                  : {},
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              userRoles={userRoles}
              {...props}
            />
            {outcomeOfStopError && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                {outcomeOfStopError}
              </FormHelperText>
            )}
          </div>
          <SingleDropDown
            disabled={locked}
            handleChange={localChange}
            stateName={"passengerTicketOutcomeType"}
            value={passengerTicketOutcomeType}
            options={passengerTicketOutcomeTypes}
            title={"Passenger\xa0Outcome\xa0Of\xa0Stop"}
            classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
        </PassengerContainer>
        <UseOfForce
          handleChange={localChange}
          uofInstance={uofInstance}
          uofInstanceTypes={uofInstanceTypes}
          uofInjuryDetails={uofInjuryDetails}
          userRoles={userRoles}
          {...props}
        />
      </FlexContainer>
    </>
  );
};

export default RaceDataUof;
